import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useFooterStyles } from '../layout';
import { Button, ContextualNavigationLayout, ContextualNavigationLayoutProps } from '@uplift/ui-v2';
import { useL10n } from '@uplift/hooks';
import { l10nEnService } from '@uplift/services';
import { FormError, FormErrorL10n } from '@uplift/types';
import { Block, DoneOutline, EventBusy, LocationOffOutlined } from '@mui/icons-material';

const getError = (l10n: typeof l10nEnService, error: FormError): FormErrorL10n =>
  l10n.forms.errors[error];

const getIconForError = (error: FormError) => {
  switch (error) {
    case 'complete':
      return <DoneOutline />;
    case 'expired':
      return <EventBusy />;
    case 'forbidden':
    case 'invalid_user':
      return <Block />;
    case 'not_found':
      return <LocationOffOutlined />;
  }
};

const getButtonText = (l10n: typeof l10nEnService, error: FormError): string => {
  if (error === 'forbidden') {
    return l10n.forms.logIn;
  }

  return l10n.forms.returnToDashboard;
};

const useErrorStyles = makeStyles({ name: 'error-page' })(theme => ({
  icon: {
    color: theme.palette.lightGold.contrastText,
    marginBottom: theme.spacing(3),
    '& svg': {
      height: '50px',
      width: '50px',
    }
  },
  footerBorder: {
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    [theme.breakpoints.up('sm')]: {
      border: 'none',
    }
  },
  footerButton: {
    [theme.breakpoints.up('sm')]: {
      width: '343px !important',
    }
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'center',
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '384px',
    }
  },
  title: {
    marginBottom: theme.spacing(1),
  }
}));

export const RenderError: React.VFC<{ formError: FormError; LayoutComponent?: React.ComponentType<ContextualNavigationLayoutProps> }> = ({ formError, LayoutComponent = ContextualNavigationLayout }) => {
  const history = useHistory();
  const { classes: errorClasses, cx } = useErrorStyles();
  const { classes: footerClasses } = useFooterStyles();
  const l10n = useL10n();

  const handleExit = useCallback(() => {
    history.push('/main');
  }, [ history ]);

  const error = useMemo(() => getError(l10n, formError), [ l10n, formError ]);

  return (
    <LayoutComponent
      headerProps={{
        leftButtonText: 'Back',
        onLeftButtonClick: ![ 'forbidden', 'invalid_user' ].includes(formError) ? handleExit : undefined
      }}
    >
      <Box className={errorClasses.root}>
        <Box className={errorClasses.icon}>
          {getIconForError(formError)}
        </Box>
        <Typography variant="h6" color="textSecondary" className={errorClasses.title}>
          {error.title}
        </Typography>
        {error.subtitle && (
          <Typography variant="body2">{error.subtitle}</Typography>
        )}
        <Box className={cx(footerClasses.footer, errorClasses.footerBorder)}>
          <Box className={cx(footerClasses.footerBase, footerClasses.centerButton)}>
            <Button variant="contained" color="secondary" onClick={handleExit} className={errorClasses.footerButton}>
              {getButtonText(l10n, formError)}
            </Button>
          </Box>
        </Box>
      </Box>
    </LayoutComponent>
  );
};
